import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, Avatar, Chip, Divider,
  IconButton, Tooltip as MuiTooltip, alpha, Menu, MenuItem, Button,
} from '@mui/material';
import {
  PeopleAlt, AccountBalanceWallet, TrendingUp, TrendingDown,
  ArrowUpward, ArrowDownward, Refresh, VerifiedUser,
  CurrencyExchange, CalendarToday, ShowChart, PieChartOutline,
  AssessmentOutlined, AccountBalance, Savings, Receipt,
  DateRange, KeyboardArrowDown, Check, Public,
} from '@mui/icons-material';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { C, CHART_COLORS, StatCard, ChartCard, CustomTooltip, fmtCurrency, fmtNum, pct } from './shared';
import TransactionsModal, { TxType } from './TransactionsModal';
import { apiFetch } from '../../utils/api';
import UserListModal from './UserListModal';

interface Breakdown { crypto: string; fiat: string; upi?: string; payIn?: string; }
interface TransactionBreakdown {
  crypto: { amount: string; count: number };
  fiat: { amount: string; count: number };
  upi?: { amount: string; count: number };
  payIn?: { amount: string; count: number };
}
interface Deposits {
  total: string; previousTotal: string; breakdown: Breakdown;
  today: { amount: string; count: number; breakdown: TransactionBreakdown };
}
interface Withdrawals {
  total: string; previousTotal: string; breakdown: Partial<Breakdown>;
  today: { amount: string; count: number; breakdown: Partial<TransactionBreakdown> };
}
interface Financials { netBalance: string; previousNetBalance: string; todayNetBalance: string; }
interface FirstDepositStats {
  registered: number; deposited: number; conversionRate: string; ftdCount: number;
  today: { registered: number; deposited: number; conversionRate: string; ftdCount: number };
}
interface DashboardData {
  totalUsers: number; previousTotalUsers: number; newUsersToday: number;
  deposits: Deposits; withdrawals: Withdrawals; financials: Financials;
  firstDeposits: FirstDepositStats;
  timestamp: string; date: string; range?: { from: string; to: string } | null;
}
interface RegistrationTrendItem { date: string; count: number; deposited?: number; }
interface FirstDepositTrendItem { date: string; count: number; }
interface DepositFunnel { registered: number; deposited: number; conversionRate: string; }
interface CountryItem { country: string; count: number; }
interface RegCityItem { city: string; country: string; count: number; }
interface UserStats {
  totalUsers: number; previousTotalUsers: number; newUsersToday: number;
  activeUsers30d: number; previousActiveUsers30d: number;
  verifiedUsers: number; previousVerifiedUsers: number;
  topCountries: CountryItem[]; registrationTrend: RegistrationTrendItem[];
  topRegistrationCountries: CountryItem[];
  topRegistrationCities: RegCityItem[];
  firstDepositTrend: FirstDepositTrendItem[];
  depositFunnel: DepositFunnel;
  timestamp: string;
}

type PeriodView = 'overall' | 'today';
type RangePreset = 'all' | 'today' | '7d' | '30d' | 'month' | 'custom';
interface DateRangeState { preset: RangePreset; from: string | null; to: string | null; }

const toYMD = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtShort = (ymd: string) => new Date(`${ymd}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const PRESET_LABELS: Record<RangePreset, string> = {
  all: 'All time', today: 'Today', '7d': 'Last 7 days', '30d': 'Last 30 days', month: 'This month', custom: 'Custom',
};

const dateInputStyle: React.CSSProperties = {
  background: '#10182E', color: '#F9F9F9', border: '1px solid #1E2D55',
  borderRadius: 8, padding: '6px 8px', fontSize: 12, colorScheme: 'dark',
};

// Compute from/to (inclusive, YMD) for a preset relative to today; 'all' → nulls.
const presetRange = (preset: RangePreset): { from: string | null; to: string | null } => {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const today = toYMD(now);
  switch (preset) {
    case 'today': return { from: today, to: today };
    case '7d': { const s = new Date(now); s.setDate(now.getDate() - 6); return { from: toYMD(s), to: today }; }
    case '30d': { const s = new Date(now); s.setDate(now.getDate() - 29); return { from: toYMD(s), to: today }; }
    case 'month': return { from: toYMD(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
    default: return { from: null, to: null };
  }
};

const DirectUsersTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<PeriodView>('overall');
  const [range, setRange] = useState<DateRangeState>({ preset: 'all', from: null, to: null });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [txModal, setTxModal] = useState<{ open: boolean; type: TxType }>({ open: false, type: 'deposit' });
  const [userListOpen, setUserListOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    totalUsers: 0, previousTotalUsers: 0, newUsersToday: 0,
    deposits: {
      total: "0", previousTotal: "0",
      breakdown: { crypto: "0", fiat: "0", upi: "0", payIn: "0" },
      today: { amount: "0", count: 0, breakdown: { crypto: { amount: "0", count: 0 }, fiat: { amount: "0", count: 0 }, upi: { amount: "0", count: 0 }, payIn: { amount: "0", count: 0 } } }
    },
    withdrawals: {
      total: "0", previousTotal: "0", breakdown: { crypto: "0", fiat: "0" },
      today: { amount: "0", count: 0, breakdown: { crypto: { amount: "0", count: 0 }, fiat: { amount: "0", count: 0 } } }
    },
    financials: { netBalance: "0", previousNetBalance: "0", todayNetBalance: "0" },
    firstDeposits: {
      registered: 0, deposited: 0, conversionRate: "0.0", ftdCount: 0,
      today: { registered: 0, deposited: 0, conversionRate: "0.0", ftdCount: 0 }
    },
    timestamp: "", date: ""
  });
  const [userStats, setUserStats] = useState<UserStats>({
    totalUsers: 0, previousTotalUsers: 0, newUsersToday: 0,
    activeUsers30d: 0, previousActiveUsers30d: 0,
    verifiedUsers: 0, previousVerifiedUsers: 0,
    topCountries: [], registrationTrend: [],
    topRegistrationCountries: [], topRegistrationCities: [],
    firstDepositTrend: [], depositFunnel: { registered: 0, deposited: 0, conversionRate: "0.0" },
    timestamp: ""
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const qs = range.from && range.to ? `?from=${range.from}&to=${range.to}` : '';
      const [dashRes, statsRes] = await Promise.allSettled([
        apiFetch<DashboardData>(`/api/admin/dashboard${qs}`),
        apiFetch<UserStats>('/api/admin/user-stats'),
      ]);
      if (dashRes.status === 'fulfilled') setDashboardData(dashRes.value);
      if (statsRes.status === 'fulfilled') setUserStats(statsRes.value);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [range]);

  useEffect(() => { fetchAll(); const i = setInterval(fetchAll, 300000); return () => clearInterval(i); }, [fetchAll]);

  const isTodayView = view === 'today';
  const rangeActive = !isTodayView && range.preset !== 'all';
  const rangeLabel = range.preset === 'custom' && range.from && range.to
    ? `${fmtShort(range.from)} – ${fmtShort(range.to)}`
    : PRESET_LABELS[range.preset];

  // The modal mirrors whatever period the cards are showing.
  const modalFrom = isTodayView ? toYMD(new Date()) : range.from;
  const modalTo = isTodayView ? toYMD(new Date()) : range.to;
  const modalLabel = isTodayView ? 'Today' : rangeLabel;
  const openTx = (type: TxType) => setTxModal({ open: true, type });

  const applyPreset = (preset: RangePreset) => {
    setRange({ preset, ...presetRange(preset) });
    setAnchorEl(null);
  };
  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    const [from, to] = customFrom <= customTo ? [customFrom, customTo] : [customTo, customFrom];
    setRange({ preset: 'custom', from, to });
    setAnchorEl(null);
  };

  // Normalize the two shapes (all-time strings vs. today's {amount,count}) into
  // a single period-driven view so every widget below reads from one source.
  const period = useMemo(() => {
    const d = dashboardData;
    if (isTodayView) {
      const dep = d.deposits.today, wd = d.withdrawals.today;
      return {
        depositsTotal: dep.amount, withdrawalsTotal: wd.amount, net: d.financials.todayNetBalance,
        depositCount: dep.count, withdrawalCount: wd.count,
        depositCrypto: dep.breakdown.crypto?.amount ?? '0', depositFiat: dep.breakdown.fiat?.amount ?? '0',
        withdrawCrypto: wd.breakdown.crypto?.amount ?? '0', withdrawFiat: wd.breakdown.fiat?.amount ?? '0',
        depositCryptoCount: dep.breakdown.crypto?.count, depositFiatCount: dep.breakdown.fiat?.count,
        withdrawCryptoCount: wd.breakdown.crypto?.count, withdrawFiatCount: wd.breakdown.fiat?.count,
      };
    }
    return {
      depositsTotal: d.deposits.total, withdrawalsTotal: d.withdrawals.total, net: d.financials.netBalance,
      depositCount: undefined as number | undefined, withdrawalCount: undefined as number | undefined,
      depositCrypto: d.deposits.breakdown.crypto ?? '0', depositFiat: d.deposits.breakdown.fiat ?? '0',
      withdrawCrypto: d.withdrawals.breakdown.crypto ?? '0', withdrawFiat: d.withdrawals.breakdown.fiat ?? '0',
      depositCryptoCount: undefined as number | undefined, depositFiatCount: undefined as number | undefined,
      withdrawCryptoCount: undefined as number | undefined, withdrawFiatCount: undefined as number | undefined,
    };
  }, [dashboardData, isTodayView]);

  const depositPie = useMemo(() => [
    { name: 'Crypto', value: parseFloat(period.depositCrypto || "0") },
    { name: 'Fiat', value: parseFloat(period.depositFiat || "0") },
  ], [period]);

  const withdrawPie = useMemo(() => [
    { name: 'Crypto', value: parseFloat(period.withdrawCrypto || "0") },
    { name: 'Fiat', value: parseFloat(period.withdrawFiat || "0") },
  ], [period]);

  // Each point = clients who registered that day, and how many of that same
  // day's cohort went on to fund their account at least once.
  const trendData = useMemo(() =>
    userStats.registrationTrend.map(item => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      users: item.count,
      funded: item.deposited ?? 0,
    })),
    [userStats]
  );

  // First-deposit conversion for whatever period the cards are showing.
  const ftd = useMemo(() => {
    const f = dashboardData.firstDeposits;
    if (!f) return { registered: 0, deposited: 0, conversionRate: '0.0', ftdCount: 0 };
    return isTodayView ? f.today : f;
  }, [dashboardData, isTodayView]);

  // Prefer geo resolved from the signup IP; users.country is self-declared on
  // the form and is null for most accounts.
  const countryData = useMemo(() => {
    const geo = userStats.topRegistrationCountries ?? [];
    return (geo.length ? geo : userStats.topCountries).slice(0, 8);
  }, [userStats]);
  const usingGeoCountries = (userStats.topRegistrationCountries ?? []).length > 0;
  const cityData = useMemo(() => (userStats.topRegistrationCities ?? []).slice(0, 8), [userStats]);
  const periodNet = parseFloat(period.net);

  // Filter for the user-list modal, aligned with how the dashboard COUNTS users
  // so the modal total always matches the card that opened it:
  //   Today   → created=today (server CURRENT_DATE — same as newUsersToday; never
  //             the client's local date, which can drift a day across timezones)
  //   Range   → startDate/endDate from the active preset/custom range
  //   Overall → no filter (all users — same as totalUsers)
  const userList = useMemo(() => {
    if (isTodayView) {
      return { filterQuery: 'created=today', title: "Today's Users", dateLabel: 'Today' };
    }
    if (rangeActive && range.from && range.to) {
      return {
        filterQuery: `startDate=${range.from}&endDate=${range.to}`,
        title: 'Users Registered',
        dateLabel: rangeLabel,
      };
    }
    return { filterQuery: '', title: 'All Users', dateLabel: null as string | null };
  }, [isTodayView, rangeActive, range.from, range.to, rangeLabel]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography sx={{ color: C.text, fontWeight: 800, fontSize: { xs: '1.3rem', sm: '1.5rem' }, lineHeight: 1.2 }}>
            Direct Users Overview
          </Typography>
          <Typography sx={{ color: C.textMuted, fontSize: '0.78rem', mt: 0.25 }}>
            Registrations, deposits & withdrawals for direct platform users
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isTodayView ? (
            <Chip
              icon={<CalendarToday sx={{ fontSize: 14 }} />}
              label={new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              size="small"
              sx={{ bgcolor: C.cardLight, color: C.textMuted, border: `1px solid ${C.border}`, fontWeight: 500, '& .MuiChip-icon': { color: C.textSecondary } }}
            />
          ) : (
            <Chip
              onClick={(e) => setAnchorEl(e.currentTarget)}
              icon={<DateRange sx={{ fontSize: 14 }} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  {rangeLabel}
                  <KeyboardArrowDown sx={{ fontSize: 16 }} />
                </Box>
              }
              size="small"
              sx={{
                cursor: 'pointer', fontWeight: 500,
                bgcolor: rangeActive ? alpha(C.primary, 0.15) : C.cardLight,
                color: rangeActive ? C.primaryLight : C.textMuted,
                border: `1px solid ${rangeActive ? C.primary : C.border}`,
                '& .MuiChip-icon': { color: rangeActive ? C.primaryLight : C.textSecondary },
                '&:hover': { borderColor: C.primary, bgcolor: rangeActive ? alpha(C.primary, 0.2) : C.cardHover },
              }}
            />
          )}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { bgcolor: C.card, border: `1px solid ${C.border}`, mt: 1, minWidth: 240 } }}
          >
            {(['all', 'today', '7d', '30d', 'month'] as RangePreset[]).map(p => (
              <MenuItem
                key={p}
                onClick={() => applyPreset(p)}
                sx={{ fontSize: '0.82rem', color: C.text, display: 'flex', justifyContent: 'space-between', gap: 2, '&:hover': { bgcolor: C.cardHover } }}
              >
                {PRESET_LABELS[p]}
                {range.preset === p && <Check sx={{ fontSize: 16, color: C.primary }} />}
              </MenuItem>
            ))}
            <Divider sx={{ borderColor: C.border, my: 0.5 }} />
            <Box sx={{ px: 2, py: 1 }}>
              <Typography sx={{ color: C.textMuted, fontSize: '0.7rem', mb: 1 }}>Custom range</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <input type="date" value={customFrom} max={customTo || undefined} onChange={e => setCustomFrom(e.target.value)} style={dateInputStyle} />
                <input type="date" value={customTo} min={customFrom || undefined} onChange={e => setCustomTo(e.target.value)} style={dateInputStyle} />
                <Button
                  size="small" variant="contained" onClick={applyCustom} disabled={!customFrom || !customTo}
                  sx={{ textTransform: 'none', bgcolor: C.primary, '&:hover': { bgcolor: C.primaryLight }, '&.Mui-disabled': { bgcolor: C.cardHover, color: C.textSecondary } }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Menu>
          <MuiTooltip title="Refresh data">
            <IconButton onClick={fetchAll} size="small" sx={{ color: C.textSecondary, bgcolor: C.cardLight, border: `1px solid ${C.border}`, '&:hover': { bgcolor: C.cardHover, borderColor: C.primary } }}>
              <Refresh fontSize="small" />
            </IconButton>
          </MuiTooltip>
        </Box>
      </Box>

      {/* Period sub-tabs: Overall vs Today */}
      <Box sx={{ display: 'inline-flex', bgcolor: C.cardLight, border: `1px solid ${C.border}`, borderRadius: 2, p: 0.5, mb: 3 }}>
        {([
          { key: 'overall', label: 'Overall' },
          { key: 'today', label: 'Today' },
        ] as { key: PeriodView; label: string }[]).map(t => (
          <Box
            key={t.key}
            onClick={() => setView(t.key)}
            sx={{
              cursor: 'pointer', px: 2.75, py: 0.75, borderRadius: 1.5, userSelect: 'none',
              fontSize: '0.8rem', fontWeight: 600, transition: 'all 200ms ease',
              bgcolor: view === t.key ? C.primary : 'transparent',
              color: view === t.key ? '#fff' : C.textMuted,
              '&:hover': { color: view === t.key ? '#fff' : C.text },
            }}
          >
            {t.label}
          </Box>
        ))}
      </Box>

      {/* Row 1: Main Stats (period-driven) */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} lg={3}>
          {isTodayView ? (
            <StatCard loading={loading} icon={<PeopleAlt />} iconBg={C.primaryLight} title="New Users Today" value={fmtNum(dashboardData.newUsersToday)} subtitle={`Total users: ${fmtNum(dashboardData.totalUsers)}`} onClick={() => setUserListOpen(true)} />
          ) : (
            <StatCard loading={loading} icon={<PeopleAlt />} iconBg={C.primaryLight} title={rangeActive ? 'Users Registered' : 'Total Users'} value={fmtNum(dashboardData.totalUsers)} subtitle={rangeActive ? rangeLabel : `New today: ${fmtNum(dashboardData.newUsersToday)}`} change={pct(dashboardData.totalUsers, dashboardData.previousTotalUsers)} onClick={() => setUserListOpen(true)} />
          )}
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            loading={loading} icon={<Savings />} iconBg={C.success}
            title={isTodayView ? "Today's Deposits" : (rangeActive ? 'Deposits' : 'Total Deposits')}
            value={fmtCurrency(period.depositsTotal)}
            subtitle={isTodayView ? `${period.depositCount ?? 0} transactions` : (rangeActive ? rangeLabel : `Today: ${fmtCurrency(dashboardData.deposits.today.amount)}`)}
            change={isTodayView ? undefined : pct(parseFloat(dashboardData.deposits.total), parseFloat(dashboardData.deposits.previousTotal))}
            onClick={() => openTx('deposit')}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            loading={loading} icon={<AccountBalanceWallet />} iconBg={C.error}
            title={isTodayView ? "Today's Withdrawals" : (rangeActive ? 'Withdrawals' : 'Total Withdrawals')}
            value={fmtCurrency(period.withdrawalsTotal)}
            subtitle={isTodayView ? `${period.withdrawalCount ?? 0} transactions` : (rangeActive ? rangeLabel : `Today: ${fmtCurrency(dashboardData.withdrawals.today.amount)}`)}
            change={isTodayView ? undefined : pct(parseFloat(dashboardData.withdrawals.total), parseFloat(dashboardData.withdrawals.previousTotal))}
            onClick={() => openTx('withdrawal')}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            loading={loading} icon={<CurrencyExchange />} iconBg="#7B5EF5"
            title="Net Balance"
            value={fmtCurrency(period.net)}
            subtitle={isTodayView ? 'Deposits − withdrawals today' : (rangeActive ? rangeLabel : `Today: ${fmtCurrency(dashboardData.financials.todayNetBalance)}`)}
            change={isTodayView ? undefined : pct(parseFloat(dashboardData.financials.netBalance), parseFloat(dashboardData.financials.previousNetBalance))}
          />
        </Grid>
      </Grid>

      {/* Row 1b: Registration → first-deposit conversion (period-driven) */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            loading={loading} icon={<PeopleAlt />} iconBg={C.primary}
            title={isTodayView ? 'Registered Today' : (rangeActive ? 'Registered' : 'Registered · All time')}
            value={fmtNum(ftd.registered)}
            subtitle={isTodayView ? 'New clients today' : (rangeActive ? rangeLabel : 'Every client on the platform')}
            onClick={() => setUserListOpen(true)}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            loading={loading} icon={<AccountBalance />} iconBg={C.success}
            title="Made First Deposit"
            value={fmtNum(ftd.deposited)}
            subtitle={`${fmtNum(ftd.registered - ftd.deposited)} still never funded`}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            loading={loading} icon={<TrendingUp />} iconBg={C.warning}
            title="Conversion Rate"
            value={`${ftd.conversionRate}%`}
            subtitle={`${fmtNum(ftd.deposited)} of ${fmtNum(ftd.registered)} registered clients`}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            loading={loading} icon={<Savings />} iconBg={C.info}
            title="First-Time Depositors"
            value={fmtNum(ftd.ftdCount)}
            subtitle={isTodayView ? 'First-ever deposit landed today' : (rangeActive ? `First-ever deposit in ${rangeLabel}` : 'Accounts funded at least once')}
          />
        </Grid>
      </Grid>

      {/* Row 2: Secondary Stats */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {isTodayView ? (
          <>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard loading={loading} icon={<ArrowUpward />} iconBg={C.success} title="Deposits · Crypto" value={fmtCurrency(period.depositCrypto)} subtitle={`${period.depositCryptoCount ?? 0} txns`} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard loading={loading} icon={<Savings />} iconBg={C.success} title="Deposits · Fiat" value={fmtCurrency(period.depositFiat)} subtitle={`${period.depositFiatCount ?? 0} txns`} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard loading={loading} icon={<ArrowDownward />} iconBg={C.error} title="Withdrawals · Crypto" value={fmtCurrency(period.withdrawCrypto)} subtitle={`${period.withdrawCryptoCount ?? 0} txns`} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard loading={loading} icon={<AccountBalanceWallet />} iconBg={C.error} title="Withdrawals · Fiat" value={fmtCurrency(period.withdrawFiat)} subtitle={`${period.withdrawFiatCount ?? 0} txns`} />
            </Grid>
          </>
        ) : rangeActive ? (
          <>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard loading={loading} icon={<ArrowUpward />} iconBg={C.success} title="Deposits · Crypto" value={fmtCurrency(period.depositCrypto)} subtitle={rangeLabel} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard loading={loading} icon={<Savings />} iconBg={C.success} title="Deposits · Fiat" value={fmtCurrency(period.depositFiat)} subtitle={rangeLabel} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard loading={loading} icon={<ArrowDownward />} iconBg={C.error} title="Withdrawals · Crypto" value={fmtCurrency(period.withdrawCrypto)} subtitle={rangeLabel} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard loading={loading} icon={<AccountBalanceWallet />} iconBg={C.error} title="Withdrawals · Fiat" value={fmtCurrency(period.withdrawFiat)} subtitle={rangeLabel} />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard loading={loading} icon={<VerifiedUser />} iconBg={C.info} title="Active Users (30d)" value={fmtNum(userStats.activeUsers30d)} subtitle={`Verification: ${userStats.totalUsers > 0 ? ((userStats.verifiedUsers / userStats.totalUsers) * 100).toFixed(1) : '0'}%`} change={pct(userStats.activeUsers30d, userStats.previousActiveUsers30d)} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard loading={loading} icon={<Receipt />} iconBg={C.warning} title="Verified Users" value={fmtNum(userStats.verifiedUsers)} subtitle={`KYC: ${userStats.totalUsers > 0 ? ((userStats.verifiedUsers / userStats.totalUsers) * 100).toFixed(1) : '0'}%`} change={pct(userStats.verifiedUsers, userStats.previousVerifiedUsers)} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard loading={loading} icon={<ArrowUpward />} iconBg={C.success} title="Deposits · Crypto" value={fmtCurrency(period.depositCrypto)} subtitle="All-time" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard loading={loading} icon={<ArrowDownward />} iconBg={C.error} title="Withdrawals · Crypto" value={fmtCurrency(period.withdrawCrypto)} subtitle="All-time" />
            </Grid>
          </>
        )}
      </Grid>

      {/* Row 3: Charts (all-time history — overall view, no active range) */}
      {!isTodayView && !rangeActive && (
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} lg={8}>
            <ChartCard title="Registrations vs. First Deposits" icon={<ShowChart sx={{ fontSize: 18 }} />} height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={C.primary} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={C.primary} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorFunded" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={C.success} stopOpacity={0.45} />
                      <stop offset="100%" stopColor={C.success} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                  <XAxis dataKey="date" tick={{ fill: C.textMuted, fontSize: 11 }} axisLine={{ stroke: C.border }} tickLine={false} />
                  <YAxis tick={{ fill: C.textMuted, fontSize: 11 }} axisLine={{ stroke: C.border }} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="users" name="Registered" stroke={C.primary} strokeWidth={2.5} fill="url(#colorUsers)" dot={false} activeDot={{ r: 5, fill: C.primary, stroke: C.text, strokeWidth: 2 }} />
                  <Area type="monotone" dataKey="funded" name="Made First Deposit" stroke={C.success} strokeWidth={2.5} fill="url(#colorFunded)" dot={false} activeDot={{ r: 5, fill: C.success, stroke: C.text, strokeWidth: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12} lg={4}>
            <ChartCard title={usingGeoCountries ? 'Top Registration Countries' : 'Top Countries'} icon={<AssessmentOutlined sx={{ fontSize: 18 }} />} height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={countryData} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                  <XAxis type="number" tick={{ fill: C.textMuted, fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="country" type="category" tick={{ fill: C.textMuted, fontSize: 11 }} axisLine={false} tickLine={false} width={70} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Users" radius={[0, 6, 6, 0]} barSize={18}>
                    {countryData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}

      {/* Row 3b: Where registrations physically come from (geo-resolved signup IPs) */}
      {!isTodayView && !rangeActive && cityData.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12}>
            <ChartCard title="Top Registration Locations" icon={<Public sx={{ fontSize: 18 }} />} height="auto">
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {cityData.map((c, i) => (
                  <Box
                    key={`${c.city}-${c.country}-${i}`}
                    sx={{
                      bgcolor: C.cardHover, border: `1px solid ${C.border}`, borderRadius: 2,
                      px: 2, py: 1.25, minWidth: 170, flex: '0 1 auto',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: CHART_COLORS[i % CHART_COLORS.length], flexShrink: 0 }} />
                      <Typography sx={{ color: C.text, fontSize: '0.85rem', fontWeight: 700 }}>{c.city}</Typography>
                    </Box>
                    <Typography sx={{ color: C.textMuted, fontSize: '0.7rem' }}>{c.country}</Typography>
                    <Typography sx={{ color: C.primaryLight, fontSize: '0.78rem', fontWeight: 600, mt: 0.25 }}>
                      {fmtNum(c.count)} client{c.count === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Typography sx={{ color: C.textMuted, fontSize: '0.68rem', mt: 1.5 }}>
                Resolved from each client's registration IP. Click any user-count card above to see per-client IP and location.
              </Typography>
            </ChartCard>
          </Grid>
        </Grid>
      )}

      {/* Row 4: Pie Charts (period-driven) */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={6}>
          <ChartCard title={`${isTodayView ? "Today's" : (rangeActive ? rangeLabel : 'Overall')} Deposit Distribution`} icon={<PieChartOutline sx={{ fontSize: 18 }} />} height={240}>
            <Box sx={{ display: 'flex', height: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={depositPie} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value" stroke="none">
                      {depositPie.map((_, i) => <Cell key={i} fill={['#2B6EF5', '#7B5EF5'][i]} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1.5, minWidth: 140 }}>
                {[{ label: 'Crypto', val: period.depositCrypto, color: '#2B6EF5' }, { label: 'Fiat', val: period.depositFiat, color: '#7B5EF5' }].map(item => (
                  <Box key={item.label} sx={{ bgcolor: C.cardHover, borderRadius: 2, p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} />
                      <Typography sx={{ color: C.textMuted, fontSize: '0.7rem' }}>{item.label}</Typography>
                    </Box>
                    <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.85rem' }}>{fmtCurrency(item.val)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title={`${isTodayView ? "Today's" : (rangeActive ? rangeLabel : 'Overall')} Withdrawal Distribution`} icon={<PieChartOutline sx={{ fontSize: 18 }} />} height={240}>
            <Box sx={{ display: 'flex', height: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={withdrawPie} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value" stroke="none">
                      {withdrawPie.map((_, i) => <Cell key={i} fill={[C.error, '#FF6B8A'][i]} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1.5, minWidth: 140 }}>
                {[{ label: 'Crypto', val: period.withdrawCrypto, color: C.error }, { label: 'Fiat', val: period.withdrawFiat, color: '#FF6B8A' }].map(item => (
                  <Box key={item.label} sx={{ bgcolor: C.cardHover, borderRadius: 2, p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} />
                      <Typography sx={{ color: C.textMuted, fontSize: '0.7rem' }}>{item.label}</Typography>
                    </Box>
                    <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.85rem' }}>{fmtCurrency(item.val)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Row 5: Activity + Financial Summary (period-driven) */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} lg={8}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: C.card, border: `1px solid ${C.border}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
              <Avatar sx={{ bgcolor: alpha(C.info, 0.12), color: C.info, width: 32, height: 32 }}>
                <AssessmentOutlined sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.95rem' }}>{isTodayView ? "Today's" : (rangeActive ? rangeLabel : 'Overall')} Activity</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ bgcolor: C.cardHover, borderRadius: 2, p: 2, border: `1px solid ${alpha(C.success, 0.2)}` }}>
                  <Typography sx={{ color: C.success, fontWeight: 700, fontSize: '0.85rem', mb: 1.5 }}>Deposits</Typography>
                  <Grid container spacing={1.5}>
                    {[
                      { label: 'Crypto', amount: period.depositCrypto, count: period.depositCryptoCount },
                      { label: 'Fiat', amount: period.depositFiat, count: period.depositFiatCount },
                    ].map(item => (
                      <Grid item xs={6} key={item.label}>
                        <Typography sx={{ color: C.textMuted, fontSize: '0.7rem' }}>{item.label}</Typography>
                        <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.9rem' }}>{fmtCurrency(item.amount)}</Typography>
                        {typeof item.count === 'number' && (
                          <Typography sx={{ color: C.textSecondary, fontSize: '0.65rem' }}>{item.count} txns</Typography>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                  <Divider sx={{ my: 1.5, borderColor: C.border }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: C.textSecondary, fontSize: '0.78rem' }}>Total</Typography>
                    <Typography sx={{ color: C.success, fontWeight: 800, fontSize: '0.9rem' }}>{fmtCurrency(period.depositsTotal)}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ bgcolor: C.cardHover, borderRadius: 2, p: 2, border: `1px solid ${alpha(C.error, 0.2)}` }}>
                  <Typography sx={{ color: C.error, fontWeight: 700, fontSize: '0.85rem', mb: 1.5 }}>Withdrawals</Typography>
                  <Grid container spacing={1.5}>
                    {[
                      { label: 'Crypto', amount: period.withdrawCrypto, count: period.withdrawCryptoCount },
                      { label: 'Fiat', amount: period.withdrawFiat, count: period.withdrawFiatCount },
                    ].map(item => (
                      <Grid item xs={6} key={item.label}>
                        <Typography sx={{ color: C.textMuted, fontSize: '0.7rem' }}>{item.label}</Typography>
                        <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.9rem' }}>{fmtCurrency(item.amount)}</Typography>
                        {typeof item.count === 'number' && (
                          <Typography sx={{ color: C.textSecondary, fontSize: '0.65rem' }}>{item.count} txns</Typography>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                  <Divider sx={{ my: 1.5, borderColor: C.border }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: C.textSecondary, fontSize: '0.78rem' }}>Total</Typography>
                    <Typography sx={{ color: C.error, fontWeight: 800, fontSize: '0.9rem' }}>{fmtCurrency(period.withdrawalsTotal)}</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: C.card, border: `1px solid ${C.border}`, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
              <Avatar sx={{ bgcolor: alpha('#7B5EF5', 0.12), color: '#7B5EF5', width: 32, height: 32 }}>
                <AccountBalance sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.95rem' }}>Financial Summary</Typography>
            </Box>
            <Box sx={{ bgcolor: C.cardHover, borderRadius: 2, p: 2, border: `1px solid ${C.border}` }}>
              <Typography sx={{ color: C.textMuted, fontSize: '0.7rem', mb: 0.5 }}>{isTodayView ? "Today's Net Balance" : (rangeActive ? `Net Balance · ${rangeLabel}` : 'All-time Net Balance')}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ color: periodNet >= 0 ? C.success : C.error, fontWeight: 800, fontSize: '1.3rem' }}>
                  {fmtCurrency(period.net)}
                </Typography>
                {periodNet >= 0 ? <TrendingUp sx={{ color: C.success, fontSize: 28 }} /> : <TrendingDown sx={{ color: C.error, fontSize: 28 }} />}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <TransactionsModal
        open={txModal.open}
        onClose={() => setTxModal(m => ({ ...m, open: false }))}
        type={txModal.type}
        from={modalFrom}
        to={modalTo}
        periodLabel={modalLabel}
      />

      <UserListModal
        open={userListOpen}
        onClose={() => setUserListOpen(false)}
        filterQuery={userList.filterQuery}
        title={userList.title}
        dateLabel={userList.dateLabel}
      />
    </Box>
  );
};

export default DirectUsersTab;
